import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, LogOut, Gift, Lock, GitBranch, Heart } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Ornament } from "../components/Ornament";
import { Monogram } from "../components/Monogram";
import { UpgradeModal } from "../components/UpgradeModal";
import { FoundingBadge } from "../components/FoundingBadge";
import { LanguageSelector } from "../components/LanguageSelector";
import NotificationBell from "../components/NotificationBell";
import FamilyInviteBlock from "../components/FamilyInviteBlock";
import { useTranslation } from "react-i18next";
import { RELATIONS, currentMonthlyTheme } from "../data/content";

export default function Dashboard() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, setUser, logout } = useAuth();
  const [family, setFamily] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [me, setMe] = useState(null);
  const [anniversaries, setAnniversaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingFamilyName, setEditingFamilyName] = useState(false);
  const [familyName, setFamilyName] = useState("");
  const [newName, setNewName] = useState("");
  const [newRelation, setNewRelation] = useState("Grandparent");
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState("");

  useEffect(() => {
    (async () => {
      try {
        if (!user) {
          const r = await api.get("/auth/me");
          setUser(r.data);
          setMe(r.data);
        } else {
          const r = await api.get("/auth/me");
          setMe(r.data);
        }
        const [fam, plist, anniv] = await Promise.all([
          api.get("/families/me"),
          api.get("/profiles"),
          api.get("/anniversaries").catch(() => ({ data: [] })),
        ]);
        setFamily(fam.data);
        setFamilyName(fam.data.name);
        setProfiles(plist.data || []);
        setAnniversaries(anniv.data || []);
      } catch (e) { navigate("/login"); } finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, []);

  const saveFamilyName = async () => {
    try {
      const res = await api.put("/families/me", { name: familyName.trim() || family.name });
      setFamily(res.data);
      setEditingFamilyName(false);
    } catch (e) {}
  };

  const tryShowAdd = () => {
    if (!me) return;
    const limit = me.profile_limit;
    if (limit != null && (me.profile_count ?? profiles.length) >= limit) {
      setUpgradeReason("profile_limit"); setUpgradeOpen(true);
      return;
    }
    setShowAdd(true);
  };

  const addProfile = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      const res = await api.post("/profiles", { name: newName.trim(), relation: newRelation });
      setProfiles((prev) => [...prev, res.data]);
      setMe((m) => m ? { ...m, profile_count: (m.profile_count || 0) + 1 } : m);
      setNewName(""); setNewRelation("Grandparent"); setShowAdd(false);
    } catch (e) {
      if (e?.response?.status === 402) {
        setShowAdd(false);
        setUpgradeReason("profile_limit"); setUpgradeOpen(true);
      }
    }
  };

  if (loading || !me) {
    return (
      <div className="min-h-screen flex items-center justify-center paper" data-testid="dashboard-loading">
        <p className="italic text-[#8C8278]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.3rem" }}>
          Reflecting on your story…
        </p>
      </div>
    );
  }

  const tier = me.subscription_tier || "free";
  const limit = me.profile_limit;
  const atLimit = limit != null && profiles.length >= limit;

  return (
    <div className="min-h-screen paper" data-testid="dashboard-page">
      <header className="max-w-5xl mx-auto px-6 sm:px-10 py-8 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-[#B8973A] text-xl" style={{ fontFamily: "Cormorant Garamond, serif" }}>✦</span>
          <span style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.2rem", letterSpacing: "0.06em" }}>The Living Book</span>
        </div>
        <div className="flex items-center gap-6">
          {tier === "free" ? (
            <button onClick={() => navigate("/subscribe")} className="label-ui text-[11px] text-[#B8973A] hover:text-[#9d8030] transition-colors" data-testid="header-upgrade-link">
              {t("profile.unlockEverything")} →
            </button>
          ) : (
            <span className="label-ui text-[11px] text-[#B8973A]" data-testid="header-tier-badge">{t("common.subscriberActive")}</span>
          )}
          <button onClick={() => navigate("/gift")} className="label-ui text-[11px] text-[#8C8278] hover:text-[#111D33] transition-colors flex items-center gap-1.5" data-testid="header-gift-link">
            <Gift size={13} /> {t("nav.gift")}
          </button>
          {tier === "family" && (
            <button onClick={() => navigate("/app/tree")} className="label-ui text-[11px] text-[#8C8278] hover:text-[#111D33] transition-colors flex items-center gap-1.5" data-testid="header-tree-link">
              <GitBranch size={13} /> Family tree
            </button>
          )}
          {false && <LanguageSelector tone="light" />}
          <NotificationBell />
          <button onClick={() => { logout(); navigate("/"); }} className="label-ui text-[11px] text-[#8C8278] hover:text-[#111D33] transition-colors flex items-center gap-2" data-testid="logout-btn">
            <LogOut size={14} /> {t("nav.signOut")}
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 sm:px-10 py-10">
        <div className="text-center mb-12">
          <p className="label-ui text-[11px] text-[#B8973A]">{t("dashboard.yourArchive")}</p>
          <FoundingBadge active={!!me?.is_founding_member} size="md" className="mt-3" />
          {!editingFamilyName ? (
            <h1
              className="text-5xl sm:text-6xl mt-4 cursor-pointer hover:text-[#1B2A4A] transition-colors"
              style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 300 }}
              onClick={() => setEditingFamilyName(true)}
              data-testid="family-name"
            >
              {family?.name}
            </h1>
          ) : (
            <div className="flex items-center justify-center gap-3 mt-4">
              <input
                value={familyName} onChange={(e) => setFamilyName(e.target.value)}
                className="text-3xl sm:text-4xl text-center bg-transparent border-b border-[#B8973A] focus:outline-none px-4 py-2"
                style={{ fontFamily: "Cormorant Garamond, serif" }} autoFocus
                data-testid="family-name-input"
              />
              <button onClick={saveFamilyName} className="btn-outline-navy" data-testid="family-name-save">Save</button>
            </div>
          )}
          <Ornament className="my-8" />
          <p className="text-[#8C8278] italic max-w-2xl mx-auto" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.2rem" }}>
            {t("dashboard.chooseStoryteller")}
          </p>
        </div>

        {/* Monthly Theme — paid tier only */}
        {me?.subscription_tier && me.subscription_tier !== "free" && profiles.length > 0 && (() => {
          const theme = currentMonthlyTheme();
          const firstProfileId = profiles[0].profile_id;
          return (
            <div
              className="mb-10 p-6 sm:p-8 bg-white hairline-cream flex flex-col sm:flex-row items-start justify-between gap-5"
              style={{ borderRadius: 4, borderColor: "#B8973A" }}
              data-testid="monthly-theme-card"
            >
              <div className="flex-1">
                <p className="label-ui text-[10px] text-[#B8973A] mb-2 tracking-[0.25em]" data-testid="monthly-theme-label">
                  THIS MONTH&apos;S THEME · {theme.title.toUpperCase()}
                </p>
                <p
                  className="text-[#1B2A4A] italic"
                  style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.35rem", lineHeight: 1.55 }}
                  data-testid="monthly-theme-prompt"
                >
                  {theme.prompt}
                </p>
              </div>
              <button
                onClick={() => navigate(`/app/profile/${firstProfileId}`)}
                className="btn-gold shrink-0"
                data-testid="monthly-theme-cta"
              >
                Answer this
              </button>
            </div>
          );
        })()}

        {anniversaries.length > 0 && (
          <div className="mb-10 p-6 bg-[#F7F0E0]" style={{ border: "1px solid #E8D5A3", borderRadius: 4 }} data-testid="anniversary-banner">
            <p className="label-ui text-[10px] text-[#B8973A] mb-3">On this day</p>
            {anniversaries.slice(0, 3).map((a) => (
              <p key={a.story_id} className="text-[#1B2A4A] italic mb-1.5" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.15rem", lineHeight: 1.55 }} data-testid={`anniversary-${a.story_id}`}>
                {a.years_ago} {a.years_ago === 1 ? "year" : "years"} ago today, {a.profile_name} captured a story.
              </p>
            ))}
            <button onClick={() => navigate(`/app/stories/${anniversaries[0].profile_id}`)} className="label-ui text-[10px] text-[#B8973A] mt-3 hover:text-[#9d8030] transition-colors" data-testid="anniversary-view-btn">
              Read it again →
            </button>
          </div>
        )}

        {tier === "free" && profiles.length > 0 && (
          <div className="mb-10 p-5 flex flex-col sm:flex-row items-center justify-between gap-4 bg-[#F7F0E0]" style={{ border: "1px solid #E8D5A3", borderRadius: 4 }} data-testid="free-tier-dashboard-banner">
            <p className="italic text-[#1B2A4A]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.1rem" }}>
              You have one profile and your Story Week responses. Become a Story Keeper to open the whole book.
            </p>
            <button onClick={() => navigate("/subscribe")} className="btn-outline-navy" data-testid="banner-upgrade-btn">See membership</button>
          </div>
        )}

        {/* Profiles grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {profiles.map((p) => (
            <button
              key={p.profile_id}
              onClick={() => navigate(`/app/profile/${p.profile_id}`)}
              className="text-left p-8 bg-white hairline-cream hover:bg-[#F7F0E0] transition-colors duration-500"
              style={{ borderRadius: 4 }}
              data-testid={`profile-card-${p.profile_id}`}
            >
              <div className="flex items-center gap-4 mb-6">
                <Monogram name={p.name} size={56} />
                <div className="flex-1">
                  <h3 className="text-2xl" style={{ fontFamily: "Cormorant Garamond, serif" }}>{p.name}</h3>
                  <p className="label-ui text-[10px] text-[#B8973A]">{p.relation}</p>
                  {p.is_memorial && (
                    <p className="label-ui text-[9px] text-[#B8973A] mt-1 flex items-center gap-1.5" data-testid={`memorial-badge-${p.profile_id}`}>
                      <Heart size={10} fill="#B8973A" /> In memory
                    </p>
                  )}
                </div>
              </div>
              <p className="text-[#8C8278] text-sm italic" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.05rem" }}>
                {p.story_count > 0 ? `${p.story_count} stor${p.story_count === 1 ? "y" : "ies"} captured` : "Their book begins here"}
              </p>
            </button>
          ))}

          {showAdd ? (
            <form onSubmit={addProfile} className="p-8 bg-white hairline space-y-4" style={{ borderRadius: 4, borderColor: "#B8973A" }} data-testid="add-profile-form">
              <div>
                <label className="label-ui text-[10px] text-[#8C8278] block mb-2">Their name</label>
                <input value={newName} onChange={(e) => setNewName(e.target.value)} required
                  className="w-full px-3 py-2 hairline-cream bg-white" style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "1rem", minHeight: 44 }}
                  data-testid="new-profile-name-input" />
              </div>
              <div>
                <label className="label-ui text-[10px] text-[#8C8278] block mb-2">Relationship</label>
                <select value={newRelation} onChange={(e) => setNewRelation(e.target.value)}
                  className="w-full px-3 py-2 hairline-cream bg-white" style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "1rem", minHeight: 44 }}
                  data-testid="new-profile-relation-select">
                  {RELATIONS.map((r) => <option key={r} value={r}>{t(`relations.${r}`)}</option>)}
                </select>
              </div>
              <div className="flex gap-3">
                <button type="submit" className="btn-primary flex-1" data-testid="new-profile-submit">{t("common.save")}</button>
                <button type="button" onClick={() => setShowAdd(false)} className="btn-outline-navy flex-1" data-testid="new-profile-cancel">{t("common.cancel")}</button>
              </div>
            </form>
          ) : (
            <button
              onClick={tryShowAdd}
              className="p-8 bg-transparent hairline-cream flex flex-col items-center justify-center gap-4 min-h-[170px] hover:bg-[#F7F0E0] transition-colors duration-500"
              style={{ borderRadius: 4, borderStyle: "dashed" }}
              data-testid="add-profile-btn"
            >
              {atLimit ? <Lock size={22} color="#B8973A" /> : <Plus size={28} color="#B8973A" />}
              <span className="label-ui text-[11px] text-[#8C8278] text-center px-4">
                {atLimit ? t("upgrade.profileBody") : t("dashboard.addFamilyMember")}
              </span>
            </button>
          )}
        </div>

        <p className="text-center text-[#8C8278] italic mt-20" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.1rem" }}>
          {t("dashboard.memoriesSurface")}
        </p>

        {/* Invite family members — Family-tier owners only */}
        {me?.subscription_tier === "family" && (
          <FamilyInviteBlock />
        )}

        <p className="text-center text-[#111D33] italic mt-6" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.25rem" }} data-testid="dashboard-manifesto">
          "We don't preserve stories. We begin conversations."
        </p>
      </main>

      <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} reason={upgradeReason} />
    </div>
  );
}
